import SpriteImage from "@PUBLIC/P/images/ico/ico_spr.png";
import styled, {css} from "styled-components";
import {RadioWrap} from "@FRAMEWORK/ui/desktop/input/RadioN";

const CouponSelectContent = styled.div`
	padding: 24px 24px 32px;
`;

const CouponSection = styled.div`
	margin-bottom: 24px;
	padding-bottom: 24px;
	border-bottom: 1px solid var(--hr-color);
`;

const CouponSectionTitle = styled.p`
	margin-bottom: 8px;
	font-family: "PT-semibold";
	font-size: 14px;
	color: var(--sub-title-color);
`;

const CouponList = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
`;

const CouponInfo = styled.div`
	display: flex;
	flex: 1;
	gap: 8px;
	overflow: hidden;

	> div:last-of-type {
		overflow: visible;
	}
`;

const CouponListItem = styled.li<{isNone?: boolean}>`
	display: flex;

	> ${RadioWrap} {
		gap: 8px;
	}

	${({isNone}) =>
		isNone
			? css`
					position: relative;
					width: 100%;
					height: 56px;
					padding: 16px;
					border: 1px solid var(--hr-color);
					border-radius: 8px;
					> ${RadioWrap} {
						width: 100%;
						> label {
							position: absolute;
							z-index: 1;
							inset: 0;
							display: flex;
							align-items: center;
							padding-top: 2px;
							padding-left: 48px;
							box-sizing: border-box;
						}
					}
				`
			: css`
					${CouponInfo} {
						padding: 16px 16px 14px 16px;
						border: 1px solid var(--line-color);
						border-right-color: var(--hr-color);
						border-radius: 8px;
					}
				`};
`;

const CouponItem = styled.label`
	display: flex;
	align-items: center;
	width: 100%;
	cursor: pointer;
`;
const CouponDownloadIcon = styled.i`
	width: 30px;
	height: 30px;
	background-image: url(${SpriteImage});
	background-position: -344px 0px;
`;

const CouponDownloadBtn = styled.button<{downloaded?: boolean; soldOut?: boolean}>`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 3px;
	width: 82px;
	height: 100%;
	margin-left: -1px;
	border: 1px solid var(--line-color);
	border-left-color: var(--hr-color);
	border-radius: 8px;
	background-color: #fff;

	> p {
		border-radius: 8px;
		background-color: var(--brand-color);
		font-size: 12px;
		font-family: "PT-bold";
		width: 54px;
		height: 20px;
		padding: 0 6px;
		line-height: 20px;
		box-sizing: border-box;
		color: #fff;
		white-space: nowrap;
	}

	${({soldOut}) =>
		soldOut &&
		css`
			pointer-events: none;

			> p {
				background-color: #bdbdbd;
				color: #fff;
			}
		`}

	${({downloaded, soldOut}) =>
		(downloaded || soldOut) &&
		css`
			> ${CouponDownloadIcon} {
				display: none;
			}
		`}

	&::before,
	&::after {
		position: absolute;
		display: block;
		width: 8px;
		height: 8px;
		background-color: #fff;
		content: "";
	}
	&::before {
		top: 8px;
		left: -4px;
	}
	&::after {
		bottom: 8px;
		left: -4px;
	}
`;

const CouponText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 6px;
	overflow: hidden;
`;

const CouponNotice = styled.p`
	position: relative;
	margin-top: 12px;
	padding-left: 10px;
	font-size: 14px;
	color: var(--caption-color);
	line-height: 1.5;

	&::before {
		position: absolute;
		top: 8px;
		left: 0;
		width: 4px;
		height: 4px;
		border-radius: 50%;
		background: var(--disable-text-color);
		content: "";
	}
`;

const CouponBadge = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
	flex: 0 0 auto;
	line-height: normal;
	color: var(--alert-color);
	font-family: "PT-bold";
	font-size: 18px;
	white-space: nowrap;
	flex-wrap: wrap;
`;

const CouponTagGroup = styled.span`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
`;

const CouponTag = styled.span<{type?: "limited" | "impending"}>`
	display: inline-flex;
	align-items: center;
	height: 18px;
	line-height: 18px;
	padding: 0 8px;
	border-radius: 8px;
	border: 1px solid var(--line-color);
	font-family: "PT-bold";
	font-size: 11px;
	white-space: nowrap;
	color: var(--caption-color);
	box-sizing: border-box;

	${({type}) =>
		type === "limited" &&
		css`
			border-color: var(--alert-color);
			background-color: var(--alert-color);
			color: #fff;
			border-radius: 8px 8px 8px 0;
		`}

	${({type}) =>
		type === "impending" &&
		css`
			border-color: var(--alert-color);
			background-color: #ffeceb;
			color: var(--alert-color);
		`}
`;

const CouponName = styled.span`
	flex: 0 0 auto;
	line-height: normal;
	font-family: "PT-semibold";
	font-size: 14px;
	color: var(--sub-title-color);
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 3;
	overflow: hidden;
	white-space: normal;
	word-break: break-all;
	text-overflow: ellipsis;
`;

const DiscountSummary = styled.div`
	position: relative;
	display: flex;
	justify-content: space-between;
`;

const DiscountSummaryLabel = styled.p`
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 16px;
	font-family: "PT-bold";
	color: var(--title-color);
	line-height: 1.5;
`;

const TooltipButton = styled.button`
	position: relative;
	width: 20px;
	height: 20px;
	background-image: url(${SpriteImage});
	background-position: -53px -371px;
	background-color: transparent;
`;

const DiscountSummaryAmount = styled.p`
	font-size: 18px;
	font-family: "PT-medium";
	color: var(--alert-color);
	line-height: 1.5;

	> em {
		font-family: "PT-bold";
	}
`;

const Tooltip = styled.div`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	bottom: 36px;
	z-index: 1;
	display: inline-flex;
	width: 100%;
	padding: 16px;
	border-radius: 8px;
	border: 1px solid var(--border-color);
	background: var(--disable-bg-color);
	box-sizing: border-box;

	&::before {
		position: absolute;
		bottom: -6px;
		left: 71px;
		transform: translateX(-50%) rotate(45deg);
		width: 11px;
		height: 11px;
		background: var(--disable-bg-color);
		border-right: 1px solid var(--border-color);
		border-bottom: 1px solid var(--border-color);
		border-radius: 0 0 2px 0;
		content: "";
		z-index: 2;
	}

	> ul {
		display: flex;
		flex-direction: column;
		gap: 6px;

		li {
			position: relative;
			padding-left: 10px;
			font-size: 14px;
			font-family: "PT-medium";
			color: var(--caption-color);
			line-height: 1.5;

			&::before {
				position: absolute;
				top: 8px;
				left: 0;
				width: 4px;
				height: 4px;
				border-radius: 50%;
				background: var(--disable-text-color);
				content: "";
			}
		}
	}
`;

const EmptyCouponbox = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
	padding: 16px;
	height: 56px;
	border-radius: 8px;
	border: 1px solid var(--hr-color);
	font-size: 14px;
	color: var(--caption-color);
	font-family: "PT-medium";
	box-sizing: border-box;
`;

export {
	CouponBadge,
	CouponTagGroup,
	CouponTag,
	CouponInfo,
	CouponItem,
	CouponList,
	CouponListItem,
	CouponName,
	CouponNotice,
	CouponDownloadBtn,
	CouponSection,
	CouponSectionTitle,
	CouponSelectContent,
	CouponText,
	DiscountSummary,
	DiscountSummaryAmount,
	DiscountSummaryLabel,
	RadioWrap,
	Tooltip,
	TooltipButton,
	EmptyCouponbox,
	CouponDownloadIcon,
};
